/**
 * Helper functions for reading metric names, tooltips, and chart captions from the
 * metric catalog. The catalog is loaded once when the dashboard starts and passed
 * to each tab.

 * `labelForMetric` uses the web label fields rather than the
 * prototype's `display_label`, matching the behaviour used by the Definitions tab
 * (`metric_headings.py`).
 */

function dedupe(parts: (string | undefined | null)[]): string[] {
    const seen = new Set<string>();
    const out: string[] = [];
    for (const p of parts) {
        const t = (p ?? "").trim();
        if (!t || seen.has(t)) continue;
        out.push(t);
        seen.add(t);
    }
    return out;
}

export function shortHelpForMetric(catalog: any, metricId: string): string {
    const metric = catalog?.metrics?.[metricId];
    if (!metric) return "";
    // Tooltip shows only the plain-language description. The SQL-style `formula` and the caveats
    // are intentionally left out (too much for a hover) — both still live on the Definitions tab.
    return (metric.plain_language ?? "").trim();
}

/**
 * Returns the display name for a metric from the metric catalog.
 *
 * A metric can have three names:
 *   - display_label          the Definitions document name  ("Daily Impacted Trips")
 *   - display_label_web      the short name for tables      ("Impacted trips")
 *   - display_label_web_long the fuller name for menus      ("Impacted supply chain legs (trips)")
 *
 * By default, this returns the long web label (`display_label_web_long`).
 * Pass `long: false` to return the short web label (`display_label_web`),
 * which is intended for table headings.
 *
 * Returns an empty string while the catalog is loading. If the metric is not
 * found, the metric ID is returned instead.
 */
export function labelForMetric(
    catalog: any,
    metricId: string,
    { long = true }: { long?: boolean } = {},
): string {
    if (!catalog?.metrics) return "";
    const metric = catalog.metrics[metricId];
    if (!metric) return metricId;
    const candidates = long
        ? [metric.display_label_web_long, metric.display_label_web]
        : [metric.display_label_web];
    for (const c of candidates) {
        if (typeof c === "string" && c.trim()) return c.trim();
    }
    return (metric.display_label ?? "").trim() || metricId;
}

export function helpForControl(catalog: any, controlId: string): string {
    const control = catalog?.controls?.[controlId];
    if (!control) return "";
    // Tooltip shows only the plain-language description; caveats stay on the Definitions tab.
    return (control.plain_language ?? "").trim();
}

export function captionForVisualization(catalog: any, vizId: string): string {
    const v = catalog?.visualizations?.[vizId];
    if (!v) return "";
    const parts: (string | undefined)[] = [v.plain_language];
    const howToRead = v.how_to_read;
    if (Array.isArray(howToRead) && howToRead.length) {
        parts.push(`How to read: ${howToRead.join(" ")}`);
    }
    const caveats = v.caveats;
    if (Array.isArray(caveats) && caveats.length) {
        parts.push(`Caveat: ${caveats[0]}`);
    }
    return dedupe(parts).join(" ");
}
